import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { ChevronDown, Check, Loader2 } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { extractApiError } from '../lib/api'
import { AccountAvatar } from './AccountAvatar'

/** Panel role for an account row — admins show their panel role; a plain
 *  user shows their assigned Work Role name (falling back to Employee). */
function panelRoleLabel(role) {
  if (role === 'super_admin') return 'Master Admin'
  if (role === 'company_admin') return 'Company Admin'
  if (role === 'sub_admin') return 'Sub Admin'
  return null
}
function accountRoleLabel(acc) {
  return panelRoleLabel(acc.role) || acc.workRoleName || 'Employee'
}

/**
 * Organisation switcher — the top-bar company pill is a dropdown of every
 * company the signed-in person belongs to (their Identity's memberships,
 * served by `/auth/accounts`). Picking another company calls the
 * account-switch endpoint, which re-mints the session and hard-reloads into
 * that company. The current company is ticked. Sits beside the user-avatar
 * menu, which keeps Profile + Sign Out.
 */
export function OrganisationSwitcher() {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const tenant = useAuthStore((s) => s.tenant)
  const accounts = useAuthStore((s) => s.accounts)
  const refreshAccounts = useAuthStore((s) => s.refreshAccounts)
  const switchAccount = useAuthStore((s) => s.switchAccount)
  const [switchingId, setSwitchingId] = useState(null)

  // Refresh the list whenever the dropdown opens — the bootstrap copy can be
  // stale after a switch / add elsewhere.
  useEffect(() => {
    if (open) void refreshAccounts()
  }, [open, refreshAccounts])

  useEffect(() => {
    if (!open) return undefined
    function onClick(e) {
      if (
        !menuRef.current?.contains(e.target) &&
        !triggerRef.current?.contains(e.target)
      ) {
        setOpen(false)
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  async function handleSwitch(acc) {
    if (acc.isCurrent || acc.available === false || switchingId) return
    setSwitchingId(acc.userId)
    try {
      // On success the store hard-reloads the page, so we never get here.
      await switchAccount(acc.userId)
    } catch (err) {
      setSwitchingId(null)
      toast.error(
        extractApiError(err).message || 'Could not switch company.',
      )
    }
  }

  const list = accounts ?? []
  const currentName = tenant?.name ?? 'Company'

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={currentName}
        className="flex items-center gap-2 h-9 pl-2 pr-2.5 rounded-full border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] hover:bg-[color:var(--color-card-hover)] transition-colors cursor-pointer"
      >
        {/* Account avatar — the navy person icon used across the profile menu
         *  and the sign-in account pickers, so every surface reads identically. */}
        <AccountAvatar name={currentName} size={24} />
        {/* Company name hidden on phones (icon-only pill) so the switcher fits
         *  the crowded mobile header without wrapping; shown from sm up. The
         *  full list (with names) is always available in the dropdown. */}
        <span className="hidden sm:block text-[color:var(--color-ink-strong)] font-semibold max-w-[12rem] truncate">
          {currentName}
        </span>
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={
            'text-[color:var(--color-ink-muted)] transition-transform ' +
            (open ? 'rotate-180' : '')
          }
        />
      </button>

      {open ? (
        <div
          ref={menuRef}
          role="menu"
          className="absolute right-0 top-full mt-2 w-80 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-xl z-40 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 pt-3 pb-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-[color:var(--color-ink-subtle)]">
              Your companies
            </span>
            <span className="text-xs font-semibold text-[color:var(--color-ink-muted)]">
              {list.length}
            </span>
          </div>
          <div className="max-h-[20rem] overflow-y-auto px-2 pb-2">
            {list.length === 0 ? (
              <p className="px-3 py-4 text-sm text-[color:var(--color-ink-muted)]">
                You only belong to this company.
              </p>
            ) : (
              list.map((acc) => (
                <button
                  key={acc.userId}
                  type="button"
                  role="menuitem"
                  disabled={
                    acc.isCurrent ||
                    acc.available === false ||
                    switchingId === acc.userId
                  }
                  title={
                    acc.available === false
                      ? acc.reason || 'Unavailable'
                      : undefined
                  }
                  onClick={() => handleSwitch(acc)}
                  className={
                    'w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left ' +
                    (acc.available === false
                      ? 'opacity-50 cursor-not-allowed'
                      : acc.isCurrent
                        ? 'bg-[color:var(--color-accent-bg)] cursor-default'
                        : 'hover:bg-[color:var(--color-canvas)] cursor-pointer')
                  }
                >
                  <AccountAvatar name={acc.tenantName} size={36} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold text-[color:var(--color-ink-strong)]">
                      {acc.tenantName || '—'}
                    </span>
                    <span className="block truncate text-xs text-[color:var(--color-ink-muted)]">
                      {acc.available === false
                        ? acc.reason || 'Unavailable'
                        : acc.pending
                          ? 'Pending invitation · tap to join'
                          : accountRoleLabel(acc)}
                    </span>
                  </span>
                  {switchingId === acc.userId ? (
                    <Loader2
                      size={16}
                      className="shrink-0 animate-spin text-[color:var(--color-ink-muted)]"
                    />
                  ) : acc.isCurrent ? (
                    <span className="shrink-0 h-5 w-5 rounded-full bg-[color:var(--color-primary)] flex items-center justify-center">
                      <Check size={12} className="text-white" />
                    </span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
